import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { api } from '../services/api';

interface BudgetLine {
  categoryId: string;
  plannedAmountEur: number;
}

interface Budget {
  month: string;
  lines: BudgetLine[];
}

interface Category {
  id: string;
  name: string;
  type: string;
}

interface ActualData {
  categoryId: string;
  totalAmountEur: number;
}

export default function BudgetPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [month, setMonth] = useState(new Date().toISOString().substring(0, 7));
  const [budget, setBudget] = useState<Budget | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [actuals, setActuals] = useState<ActualData[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const formatEur = (amount: number) =>
    new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(amount);

  useEffect(() => {
    api.get<Category[]>('/api/categories').then(setCategories);
  }, []);

  useEffect(() => {
    Promise.all([
      api.get<Budget>(`/api/budget?month=${month}`),
      api.get<{ byCategory: ActualData[] }>(`/api/reports/summary?month=${month}`),
    ]).then(([budgetData, summaryData]) => {
      setBudget(budgetData);
      setActuals(summaryData.byCategory || []);
    });
  }, [month]);

  const updateLine = (categoryId: string, amount: number) => {
    if (!budget) return;
    const lines = [...budget.lines];
    const idx = lines.findIndex((l) => l.categoryId === categoryId);
    if (idx >= 0) {
      lines[idx].plannedAmountEur = amount;
    } else {
      lines.push({ categoryId, plannedAmountEur: amount });
    }
    setBudget({ ...budget, lines });
  };

  const handleSave = async () => {
    if (!budget) return;
    setSaving(true);
    try {
      await api.put(`/api/budget?month=${month}`, { lines: budget.lines });
      setMessage('Budjetti tallennettu');
    } catch (err: any) {
      setMessage(`Virhe: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSuggest = async () => {
    try {
      const suggestion = await api.post<{ lines: BudgetLine[] }>(
        `/api/budget/suggest?month=${month}`,
      );
      setBudget({ month, lines: suggestion.lines });
      setMessage('Budjettiehdotus asetettu — tarkista ja tallenna');
    } catch (err: any) {
      setMessage(`Virhe: ${err.message}`);
    }
  };

  const getActual = (categoryId: string) => {
    const found = actuals.find((a) => a.categoryId === categoryId);
    return found ? Math.abs(found.totalAmountEur) : 0;
  };

  const getPlanned = (categoryId: string) => {
    const line = budget?.lines.find((l) => l.categoryId === categoryId);
    return line?.plannedAmountEur || 0;
  };

  // Show categories that have either a budget line or actual spending
  const relevantCats = categories.filter(
    (cat) =>
      cat.type !== 'income' &&
      (getPlanned(cat.id) > 0 || getActual(cat.id) > 0),
  );
  // Also include budgeted cats with no actuals
  const allBudgetCatIds = new Set(budget?.lines.map((l) => l.categoryId) || []);
  const expenseCats = categories.filter(
    (cat) => cat.type !== 'income' && (relevantCats.some((r) => r.id === cat.id) || !allBudgetCatIds.has(cat.id)),
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          Budjetti
        </Typography>
        <TextField
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        />
      </Box>

      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Button variant="outlined" startIcon={<AutoFixHighIcon />} onClick={handleSuggest}>
          Ehdota budjettia
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Tallennetaan...' : 'Tallenna budjetti'}
        </Button>
      </Box>

      {message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}

      {isMobile ? (
        // Mobile: Card-based layout
        <Box>
          {relevantCats.length === 0 && (
            <Typography color="text.secondary">
              Ei vielä budjettitietoja. Napsauta "Ehdota budjettia" tai syötä summat manuaalisesti.
            </Typography>
          )}
          {categories
            .filter((cat) => cat.type !== 'income')
            .map((cat) => {
              const planned = getPlanned(cat.id);
              const actual = getActual(cat.id);
              if (planned === 0 && actual === 0) return null;
              const diff = planned - actual;
              return (
                <Card key={cat.id} sx={{ mb: 1.5 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography fontWeight={600}>{cat.name}</Typography>
                    <Box display="flex" gap={2} mt={1} alignItems="center">
                      <TextField
                        label="Suunniteltu"
                        type="number"
                        value={planned || ''}
                        onChange={(e) => updateLine(cat.id, parseFloat(e.target.value) || 0)}
                        size="small"
                        sx={{ width: 120 }}
                      />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Toteuma: {formatEur(actual)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={diff >= 0 ? 'success.main' : 'error.main'}
                        >
                          {diff >= 0 ? 'Alle' : 'Yli'}: {formatEur(Math.abs(diff))}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
        </Box>
      ) : (
        // Desktop: Table
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kategoria</TableCell>
                <TableCell align="right">Suunniteltu</TableCell>
                <TableCell align="right">Toteuma</TableCell>
                <TableCell align="right">Erotus</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories
                .filter((cat) => cat.type !== 'income')
                .map((cat) => {
                  const planned = getPlanned(cat.id);
                  const actual = getActual(cat.id);
                  if (planned === 0 && actual === 0) return null;
                  const diff = planned - actual;
                  return (
                    <TableRow key={cat.id}>
                      <TableCell>{cat.name}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={planned || ''}
                          onChange={(e) =>
                            updateLine(cat.id, parseFloat(e.target.value) || 0)
                          }
                          size="small"
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatEur(actual)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: diff >= 0 ? 'success.main' : 'error.main' }}
                      >
                        {formatEur(diff)}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
